import React, { useState } from "react";
import { motion } from "framer-motion";
import { BarChart3, FileCheck2, Rocket, Sparkles } from "lucide-react";
import ExchangePilotForm from "./ExchangePilotForm";

const STEPS = [
  { id: "launch", Icon: Rocket, when: "Week 2", title: "Launch", body: "Life Pass live for your first residents and partners." },
  { id: "dashboards", Icon: BarChart3, when: "Day 30", title: "Live dashboards", body: "Cost avoidance, beds, and outcomes in real time." },
  { id: "report", Icon: FileCheck2, when: "Day 90", title: "Outcomes report", body: "A board-ready report on every dollar and every life." },
];

export const PILOT_CUES = [
  { phrase: "every partner", target: "exchange-pilot-step-launch" },
  { phrase: "prove it", target: "exchange-pilot-step-dashboards" },
  { phrase: "ninety-day pilot", target: "exchange-pilot-cta" },
];

function Steps() {
  return (
    <ol className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-3">
      {STEPS.map(({ id, Icon, when, title, body }, i) => (
        <motion.li key={id} data-testid={`exchange-pilot-step-${id}`}
          initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.12, duration: 0.5 }}
          className="relative rounded-2xl border border-[#D4AF37]/25 bg-[#120C1C]/80 p-5">
          <div className="flex items-center gap-2">
            <Icon className="w-4 h-4 text-[#F4D88A]" />
            <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#C9A227]">{when}</span>
          </div>
          <div className="mt-2 text-white font-semibold">{title}</div>
          <p className="mt-1 text-xs text-slate-400 leading-relaxed">{body}</p>
        </motion.li>
      ))}
    </ol>
  );
}

// Beat 11 — closing call to action: book a 90-day pilot.
export default function ExchangePilotSection() {
  const [open, setOpen] = useState(false);
  return (
    <section data-testid="exchange-pilot-section" className="relative mt-8 mb-16 overflow-hidden rounded-3xl border border-[#D4AF37]/40 bg-[#0A0612] p-6 md:p-10">
      <div aria-hidden className="pointer-events-none absolute -top-40 left-1/3 h-96 w-96 rounded-full bg-[#D4AF37]/15 blur-3xl" />
      <div className="relative flex gap-6">
        <div className="flex-1 min-w-0">
          <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#C9A227]">// 11 · Your Pilot</div>
          <h2 className="mt-2 text-3xl md:text-5xl font-extrabold tracking-tight bg-gradient-to-b from-white to-[#F4D88A] bg-clip-text text-transparent">
            From crisis to self-sufficiency.
          </h2>
          <p className="mt-3 max-w-xl text-slate-300">Every partner, every resident, one Life Pass. Let&apos;s prove it in your community with a 90-day pilot.</p>
          <Steps />
          {!open && (
            <button type="button" data-testid="exchange-pilot-cta" onClick={() => setOpen(true)}
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#C9A227] to-[#F4D88A] px-8 py-4 text-lg font-extrabold text-[#0A0612] shadow-[0_0_40px_rgba(212,175,55,0.35)] hover:brightness-110 hover:-translate-y-0.5 active:scale-[0.98] transition-[filter,transform]">
              <Sparkles className="w-5 h-5" /> Book a Pilot
            </button>
          )}
          {open && <ExchangePilotForm />}
        </div>
        <aside data-testid="exchange-pilot-dock" className="hidden xl:flex w-[440px] shrink-0 rounded-2xl border border-dashed border-[#D4AF37]/25 bg-[#D4AF37]/[0.03] p-6 flex-col justify-center">
          <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-[#C9A227]">90 days · one community</div>
          <p className="mt-3 text-sm italic text-slate-300">Launch in two weeks. Live dashboards by day 30. Outcomes by day 90.</p>
        </aside>
      </div>
    </section>
  );
}
